
import { UserButton, useUser } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation, type Location } from "react-router-dom";
import { Menu } from "lucide-react";
import { useState } from "react";
interface AuthLayoutProps {
  children: React.ReactNode;
}

/* Single source of truth for the shell's destinations — the desktop bar and the
   collapsible menu both render from this list so they cannot drift apart.
   Contacts is a tab of /marketing, so it and Marketing decide "current" from
   the ?tab= query as well as the path. */
const isContactsTab = (location: Location) =>
  location.pathname === "/marketing" && new URLSearchParams(location.search).get("tab") === "contacts";

const NAV_ITEMS: { path: string; label: string; isActive: (location: Location) => boolean }[] = [{
  path: "/",
  label: "Orders",
  isActive: l => l.pathname === "/"
}, {
  path: "/marketing?tab=contacts",
  label: "Contacts",
  isActive: isContactsTab
}, {
  path: "/insights",
  label: "Insights",
  isActive: l => l.pathname === "/insights"
}, {
  path: "/revenue-plays",
  label: "Revenue Plays",
  isActive: l => l.pathname === "/revenue-plays"
}, {
  path: "/marketing",
  label: "Marketing",
  isActive: l => l.pathname === "/marketing" && !isContactsTab(l)
}, {
  path: "/settings",
  label: "Settings",
  isActive: l => l.pathname === "/settings"
}, {
  path: "/offer-designer",
  label: "Offer Designer",
  isActive: l => l.pathname === "/offer-designer"
}, {
  path: "/box-topper",
  label: "Box Topper",
  isActive: l => l.pathname === "/box-topper"
}, {
  path: "/orderspy",
  label: "orderSpy",
  isActive: l => l.pathname === "/orderspy"
}];
const AuthLayout = ({
  children
}: AuthLayoutProps) => {
  const {
    user
  } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navClass = (active: boolean, extra?: string) => {
    const state = active ? "bg-brand-red hover:bg-brand-red/90 text-brand-white" : "text-muted-foreground hover:bg-muted";
    return extra ? `${extra} ${state}` : state;
  };
  return <div className="min-h-screen bg-background">
    {/* Top Navigation */}
    <header className="bg-card border-b border-border px-safe pt-safe pb-3 shadow-sm">
      <div className="container-app">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 sm:space-x-6">
            <h1 className="text-lg sm:text-xl font-bold text-brand-red">Order Portal</h1>

            {/* Desktop Navigation — the full 9-item bar needs ~1240px, so it only
                appears at xl (1280+). Laptop/tablet/phone use the menu below. */}
            <nav className="hidden xl:flex space-x-4">
              {NAV_ITEMS.map(({
                path,
                label,
                isActive
              }) => <Button key={path} variant={isActive(location) ? "default" : "ghost"} aria-current={isActive(location) ? "page" : undefined} onClick={() => navigate(path)} className={navClass(isActive(location))}>
                {label}
              </Button>)}
            </nav>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Menu Button (below the xl nav breakpoint) */}
            <Button variant="ghost" size="sm" aria-label="Toggle navigation menu" aria-expanded={mobileMenuOpen} className="xl:hidden text-muted-foreground hover:bg-muted" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <Menu className="h-5 w-5" />
            </Button>

            {/* User Info - shown alongside the full nav at xl+ */}
            <span className="hidden xl:block text-sm text-muted-foreground">
              Welcome, {user?.firstName || 'Admin'}
            </span>
            <UserButton />
          </div>
        </div>

        {/* Collapsible Navigation Menu (below xl) */}
        {mobileMenuOpen && <div className="xl:hidden mt-4 pb-4 border-t border-border pt-4">
          <nav className="flex flex-col space-y-2">
            {NAV_ITEMS.map(({
              path,
              label,
              isActive
            }) => <Button key={path} variant={isActive(location) ? "default" : "ghost"} aria-current={isActive(location) ? "page" : undefined} onClick={() => {
              navigate(path);
              setMobileMenuOpen(false);
            }} className={navClass(isActive(location), "justify-start h-12")}>
              {label}
            </Button>)}

            {/* Mobile User Info */}
            <div className="pt-2 text-sm text-muted-foreground px-3">
              Welcome, {user?.firstName || 'Admin'}
            </div>
          </nav>
        </div>}
      </div>
    </header>

    {/* Main Content */}
    <main className="px-safe pt-4 sm:pt-6 pb-safe bg-background">
      <div className="container-app">
        {children}
      </div>
    </main>
  </div>;
};
export default AuthLayout;
